import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RSSItem } from '../types/rss';

// Hook para debounce de atualizações
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Hook para throttle de funções
const useThrottle = (callback: Function, delay: number) => {
  const lastRun = useRef(Date.now());
  
  return useCallback((...args: any[]) => {
    if (Date.now() - lastRun.current >= delay) {
      callback(...args);
      lastRun.current = Date.now();
    }
  }, [callback, delay]);
};

interface NewsTickerProps {
  items: RSSItem[];
  speed?: number; // Velocidade da animação em segundos
  height?: string;
  backgroundColor?: string;
  textColor?: string;
  onCurrentItemChange?: (index: number) => void;
}

// Removendo keyframes CSS - agora usaremos JavaScript para controle contínuo

// Animação de fade-in para novas notícias
const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animação de transição suave entre buffers
const smoothTransition = keyframes`
  0% {
    opacity: 0.8;
    filter: blur(1px);
  }
  50% {
    opacity: 0.9;
    filter: blur(0.5px);
  }
  100% {
    opacity: 1;
    filter: blur(0);
  }
`;

const TickerContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['height', 'backgroundColor'].includes(prop)
})<{ height: string; backgroundColor: string }>`
  width: 100%;
  height: ${props => props.height};
  background: #000000;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  border-top: 2px solid #e74c3c;
  border-bottom: 2px solid #e74c3c;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const TickerContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['textColor', 'isPaused'].includes(prop)
})<{ textColor: string; isPaused: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  color: ${props => props.textColor};
  font-weight: 500;
  font-size: 14px;
  padding: 0 20px;
  padding-left: 120px;
  will-change: transform;
  transition: opacity 0.3s ease;
  width: max-content; /* Largura dinâmica baseada no conteúdo */
  min-width: 200%; /* Mínimo para garantir rolagem contínua */
  will-change: transform, opacity;
`;

const NewsItem = styled.span.withConfig({
  shouldForwardProp: (prop) => !['isNew', 'animationDelay', 'isActive'].includes(prop)
})<{ isNew?: boolean; animationDelay?: number; isActive?: boolean }>`
  margin-right: 80px;
  display: inline-flex;
  align-items: center;
  max-width: none;
  text-transform: uppercase;
  background: ${props => props.isActive ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.isActive ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 12px 16px;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.isActive ? '0 6px 25px rgba(59, 130, 246, 0.3)' : '0 2px 10px rgba(0, 0, 0, 0.1)'};
  
  animation: ${props => props.isNew ? css`${fadeIn} 0.6s ease-out` : 'none'};
  animation-delay: ${props => props.animationDelay ? `${props.animationDelay}ms` : '0ms'};
  animation-fill-mode: both;
  
  /* Transições suaves para todas as propriedades */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.isActive ? 'scale(1.05) translateY(-2px)' : 'scale(1)'};
  
  /* Otimizações de performance */
  will-change: ${props => props.isNew || props.isActive ? 'opacity, transform, background-color, border-color, box-shadow' : 'auto'};
  contain: layout style paint; /* CSS containment para melhor performance */

  &::before {
    content: '🔴';
    margin-right: 8px;
    font-size: 8px;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }
  
  ${props => props.isNew && css`
    position: relative;
    
    &::before {
      content: '🟢';
      animation: pulse 2s ease-in-out infinite;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: -3px;
      right: -3px;
      width: 10px;
      height: 10px;
      background: linear-gradient(45deg, #ef4444, #f97316);
      border-radius: 50%;
      animation: pulse 2s ease-in-out infinite;
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }
  `}
  
  /* Hover effect suave */
  &:hover {
    transform: ${props => props.isActive ? 'scale(1.08) translateY(-3px)' : 'scale(1.02) translateY(-1px)'};
    box-shadow: ${props => props.isActive ? '0 8px 30px rgba(59, 130, 246, 0.4)' : '0 4px 15px rgba(0, 0, 0, 0.15)'};
  }
`;

// Componente memoizado para melhor performance
const MemoizedNewsItem = React.memo<{
  item: RSSItem;
  index: number;
  isNew: boolean;
  keyPrefix: string;
  isActive?: boolean;
}>(({ item, index, isNew, keyPrefix, isActive = false }) => {
  const animationDelay = isNew ? index * 100 : 0;
  
  return (
    <NewsItem 
      key={`${keyPrefix}-${index}`}
      data-news-item
      isNew={isNew}
      isActive={isActive}
      animationDelay={animationDelay}
    >
      <strong>{item.title}</strong>
      {item.description && (
        <span style={{ marginLeft: '10px', fontWeight: 'normal', opacity: 0.9, transition: 'opacity 0.3s ease' }}>
          - {item.description}
        </span>
      )}
    </NewsItem>
  );
}, (prevProps, nextProps) => {
  // Comparação customizada para evitar re-renders desnecessários
  return (
    prevProps.item.link === nextProps.item.link &&
    prevProps.isNew === nextProps.isNew &&
    prevProps.index === nextProps.index &&
    prevProps.keyPrefix === nextProps.keyPrefix &&
    prevProps.isActive === nextProps.isActive
  );
});

const TickerLabel = styled.div.withConfig({
  shouldForwardProp: (prop) => !['textColor'].includes(prop)
})<{ textColor: string }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #e74c3c, #c0392b);
  color: white;
  padding: 0 15px;
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  z-index: 1001;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.2);
`;

const NewsTicker: React.FC<NewsTickerProps> = ({
  items,
  speed = 60,
  height = '40px',
  backgroundColor = '#1a1a1a',
  textColor = '#ffffff',
  onCurrentItemChange
}) => {
  // Debug: Log da velocidade recebida
  console.log('NewsTicker speed prop:', speed);
  const containerRef = useRef<HTMLDivElement>(null);
  const tickerContentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const positionRef = useRef<number>(0);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [animationOffset, setAnimationOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const previousItemsRef = useRef<RSSItem[]>([]);
  const stableItemsRef = useRef<RSSItem[]>([]);
  
  // Sistema de buffer duplo para atualizações sem interrupção
  const [activeBuffer, setActiveBuffer] = useState<'primary' | 'secondary'>('primary');
  const [primaryBuffer, setPrimaryBuffer] = useState<RSSItem[]>([]);
  const [secondaryBuffer, setSecondaryBuffer] = useState<RSSItem[]>([]);
  const [isBufferReady, setIsBufferReady] = useState(false);
  const [newItemsMap, setNewItemsMap] = useState<Set<string>>(new Set());
  const [lastKnownUpdateTime, setLastKnownUpdateTime] = useState<number>(0);
  const animationStateRef = useRef({ position: 0, timestamp: 0 });
  
  // Sistema de buffer duplo para atualizações sem interrupção
  const updateBuffer = useCallback((newItems: RSSItem[]) => {
    if (!newItems || newItems.length === 0) return;
    
    // Captura o estado atual da animação antes de atualizar
    const container = containerRef.current;
    if (container) {
      const tickerContent = container.querySelector('[data-ticker-content]') as HTMLElement;
      if (tickerContent) {
        const computedStyle = window.getComputedStyle(tickerContent);
        const transform = computedStyle.transform;
        
        let currentPosition = 0;
        if (transform && transform !== 'none') {
          const matrixValues = transform.match(/matrix.*\((.*)\)/);
          if (matrixValues) {
            const values = matrixValues[1].split(', ');
            currentPosition = Math.abs(parseFloat(values[4]) || 0);
          }
        }
        
        animationStateRef.current = {
          position: currentPosition,
          timestamp: Date.now()
        };
      }
    }
    
    // Atualiza o buffer inativo
    if (activeBuffer === 'primary') {
      setSecondaryBuffer(newItems);
    } else {
      setPrimaryBuffer(newItems);
    }
    
    setIsBufferReady(true);
  }, [activeBuffer]);
  
  // Alterna entre buffers de forma suave
  const switchBuffer = useCallback(() => {
    if (!isBufferReady) return;
    
    setIsTransitioning(true);
    
    // Calcula o offset baseado na posição atual da animação
    const currentState = animationStateRef.current;
    const timeDiff = Date.now() - currentState.timestamp;
    const animationSpeed = speed * 1000; // Converte para ms
    const progressPerMs = 100 / animationSpeed; // Progresso por ms
    const currentProgress = (currentState.position / window.innerWidth) * 100;
    const adjustedProgress = (currentProgress + (timeDiff * progressPerMs)) % 100;
    
    setAnimationOffset(adjustedProgress);
    
    // Alterna o buffer ativo
    setActiveBuffer(prev => prev === 'primary' ? 'secondary' : 'primary');
    setIsBufferReady(false);
    
    // Remove a transição após sincronizar
    setTimeout(() => {
      setIsTransitioning(false);
      setAnimationOffset(0);
    }, 50);
  }, [isBufferReady, speed]);
  
  // Debounce das atualizações do feed para evitar re-renders excessivos
  const debouncedItems = useDebounce(items, 300);
  
  // Memoização otimizada dos itens para exibição
  const displayItems = useMemo(() => {
    const currentBuffer = activeBuffer === 'primary' ? primaryBuffer : secondaryBuffer;
    const bufferItems = currentBuffer.length > 0 ? currentBuffer : stableItemsRef.current;
    
    // Retorna todos os itens sem limitação artificial
    return bufferItems;
  }, [activeBuffer, primaryBuffer, secondaryBuffer]);
  
  // Memoização dos itens duplicados para rolagem contínua
  const duplicatedItems = useMemo(() => {
    // Duplicar apenas os itens necessários para manter a rolagem fluida
    return [...displayItems, ...displayItems];
  }, [displayItems]);
  
  // Função para centralizar uma notícia específica na viewport com controle de pausa
  const scrollToItem = useCallback((itemIndex: number) => {
    if (!containerRef.current || !tickerContentRef.current || isScrolling) return;
    
    setIsScrolling(true);
    const containerWidth = containerRef.current.offsetWidth;
    const newsItems = tickerContentRef.current.querySelectorAll('[data-news-item]');
    const originalItemsCount = Math.floor(newsItems.length / 2);
    
    if (itemIndex >= originalItemsCount) {
      setIsScrolling(false);
      return;
    }
    
    // Calcula a posição do item com cache otimizado
    let itemOffset = 0;
    for (let i = 0; i < itemIndex; i++) {
      const item = newsItems[i] as HTMLElement;
      itemOffset += item.offsetWidth + 80; // margin-right
    }
    
    const targetItem = newsItems[itemIndex] as HTMLElement;
    const itemWidth = targetItem.offsetWidth;
    const itemCenter = itemOffset + (itemWidth / 2);
    const viewportCenter = containerWidth / 2;
    const targetPosition = itemCenter - viewportCenter;
    
    // Smooth scroll para a posição alvo com controle refinado
    const currentPos = Math.abs(positionRef.current);
    const distance = targetPosition - currentPos;
    
    // Se a distância for muito pequena, não faz scroll
    if (Math.abs(distance) < 50) {
      setIsScrolling(false);
      return;
    }
    
    // Animação suave para a nova posição
    let startTime: number;
    const duration = Math.min(1200, Math.max(600, Math.abs(distance) * 2)); // Duração adaptativa
    
    const smoothScroll = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function mais suave (ease-in-out-cubic)
      const easeProgress = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      const newPosition = currentPos + (distance * easeProgress);
      positionRef.current = -newPosition;
      
      if (tickerContentRef.current) {
        tickerContentRef.current.style.transform = `translate3d(${-newPosition}px, 0, 0)`;
        tickerContentRef.current.style.transition = 'none';
      }
      
      if (progress < 1) {
        requestAnimationFrame(smoothScroll);
      } else {
        // Finaliza o scroll e retoma a animação normal
        setTimeout(() => {
          setIsScrolling(false);
        }, 200); // Pequena pausa antes de retomar
      }
    };
    
    requestAnimationFrame(smoothScroll);
  }, [isScrolling]);

  // Sistema de debouncing inteligente para otimização
  const debouncedActionsRef = useRef({
    updateIndex: null as NodeJS.Timeout | null,
    scrollToItem: null as NodeJS.Timeout | null,
    performanceCheck: null as NodeJS.Timeout | null
  });

  // Fallbacks para navegadores antigos e otimizações cross-browser
  const browserCompatibility = useMemo(() => {
    const supportsRequestAnimationFrame = typeof requestAnimationFrame !== 'undefined';
    const supportsPerformanceNow = typeof performance !== 'undefined' && typeof performance.now === 'function';
    const supportsTransform3d = (() => {
      const el = document.createElement('div');
      el.style.transform = 'translate3d(0,0,0)';
      return el.style.transform !== '';
    })();
    const supportsWillChange = (() => {
      const el = document.createElement('div');
      return 'willChange' in el.style;
    })();
    const supportsContain = (() => {
      const el = document.createElement('div');
      return 'contain' in el.style;
    })();
    
    return {
      supportsRequestAnimationFrame,
      supportsPerformanceNow,
      supportsTransform3d,
      supportsWillChange,
      supportsContain,
      // Fallback functions with proper context binding
      getTime: supportsPerformanceNow ? performance.now.bind(performance) : Date.now.bind(Date),
      requestFrame: supportsRequestAnimationFrame ? requestAnimationFrame.bind(window) : (callback: FrameRequestCallback) => setTimeout(callback, 16),
      cancelFrame: supportsRequestAnimationFrame ? cancelAnimationFrame.bind(window) : clearTimeout.bind(window)
    };
  }, []);

  // Sistema de performance e FPS adaptativo com métricas avançadas
  const performanceRef = useRef({
    frameCount: 0,
    lastFpsTime: performance.now(),
    currentFps: 60,
    targetFps: 60,
    frameTime: 16.67, // 60fps = 16.67ms por frame
    adaptiveFps: true,
    lowPerformanceMode: false,
    // Métricas avançadas
    frameTimeHistory: [] as number[],
    averageFrameTime: 16.67,
    jankFrames: 0,
    smoothFrames: 0,
    performanceScore: 100,
    lastFrameTime: performance.now(),
    memoryUsage: 0,
    cpuUsage: 0,
    // Thresholds adaptativos
    jankThreshold: 33.33, // 30fps
    smoothThreshold: 16.67, // 60fps
    performanceCheckInterval: 1000
  });

  // Detecção de dispositivos e capacidades do navegador
  const deviceCapabilities = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);
    const isSafari = /safari/i.test(userAgent) && !/chrome/i.test(userAgent);
    const isChrome = /chrome/i.test(userAgent);
    const isFirefox = /firefox/i.test(userAgent);
    const isEdge = /edge/i.test(userAgent);
    
    // Detecta suporte a hardware acceleration
    const supportsHardwareAcceleration = (() => {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return !!gl;
    })();
    
    // Detecta cores de CPU (aproximado)
    const cpuCores = navigator.hardwareConcurrency || 4;
    
    // Detecta memória disponível (se suportado)
    const deviceMemory = (navigator as any).deviceMemory || 4;
    
    // Detecta conexão de rede
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    const networkSpeed = connection ? connection.effectiveType : '4g';
    
    return {
      isMobile,
      isTablet,
      isDesktop: !isMobile && !isTablet,
      isIOS,
      isAndroid,
      browser: {
        safari: isSafari,
        chrome: isChrome,
        firefox: isFirefox,
        edge: isEdge
      },
      hardware: {
        supportsHardwareAcceleration,
        cpuCores,
        deviceMemory,
        networkSpeed
      },
      // Configurações otimizadas baseadas no dispositivo
      optimizations: {
        targetFps: isMobile ? 30 : 60,
        useHardwareAcceleration: supportsHardwareAcceleration && !isIOS, // iOS tem problemas com algumas otimizações
        enableSubPixelRendering: !isMobile,
        useWillChange: isChrome || isEdge,
        enableContainment: isChrome || isFirefox,
        debounceTime: isMobile ? 200 : 100,
        throttleTime: isMobile ? 200 : 150
      }
    };
  }, []);

  // Debouncing adaptativo baseado na performance do dispositivo e capacidades
  const getOptimalDebounceTime = useCallback(() => {
    const perf = performanceRef.current;
    const deviceOpts = deviceCapabilities.optimizations;
    
    if (perf.lowPerformanceMode) {
      return Math.max(deviceOpts.debounceTime, 200); // Mais tempo em dispositivos lentos
    } else if (perf.currentFps >= 55) {
      return Math.min(deviceOpts.debounceTime, 100); // Menos tempo em dispositivos rápidos
    }
    return deviceOpts.debounceTime; // Baseado no dispositivo
   }, [deviceCapabilities.optimizations]);

   // Função de atualização com debouncing inteligente
    const updateIndexWithDebounce = useCallback((newIndex: number) => {
      // Cancela chamadas anteriores
      if (debouncedActionsRef.current.updateIndex) {
        clearTimeout(debouncedActionsRef.current.updateIndex);
      }
      if (debouncedActionsRef.current.scrollToItem) {
        clearTimeout(debouncedActionsRef.current.scrollToItem);
      }

      if (newIndex !== currentItemIndex) {
        // Atualiza o índice imediatamente para responsividade
        setCurrentItemIndex(newIndex);
        console.log(`Item em destaque: ${newIndex} - ${displayItems[newIndex]?.title?.substring(0, 50)}...`);
        
        // Debounce do scroll para evitar múltiplas chamadas
        const debounceTime = getOptimalDebounceTime();
        debouncedActionsRef.current.scrollToItem = setTimeout(() => {
          scrollToItem(newIndex);
        }, Math.min(debounceTime * 0.3, 50)); // Scroll mais rápido que o throttle
      }
    }, [scrollToItem, getOptimalDebounceTime, currentItemIndex, displayItems]);

    // Throttled function otimizada
    const throttledUpdateIndex = useThrottle(updateIndexWithDebounce, getOptimalDebounceTime());

    // Cálculos de performance em tempo real
  const calculatePerformanceMetrics = useCallback((currentTime: number) => {
    const perf = performanceRef.current;
    const frameTime = currentTime - perf.lastFrameTime;
    
    // Atualiza histórico de frame times
    perf.frameTimeHistory.push(frameTime);
    if (perf.frameTimeHistory.length > 60) {
      perf.frameTimeHistory.shift(); // Mantém apenas os últimos 60 frames
    }
    
    // Calcula média de frame time
    perf.averageFrameTime = perf.frameTimeHistory.reduce((a, b) => a + b, 0) / perf.frameTimeHistory.length;
    
    // Detecta frames com jank (>33ms = <30fps)
    if (frameTime > perf.jankThreshold) {
      perf.jankFrames++;
    } else if (frameTime <= perf.smoothThreshold) {
      perf.smoothFrames++;
    }
    
    // Calcula score de performance (0-100)
    const jankRatio = perf.jankFrames / (perf.jankFrames + perf.smoothFrames + 1);
    perf.performanceScore = Math.max(0, 100 - (jankRatio * 100));
    
    // Estimativa de uso de memória (aproximada)
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      perf.memoryUsage = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
    }
    
    perf.lastFrameTime = currentTime;
    
    return {
      fps: perf.currentFps,
      averageFrameTime: perf.averageFrameTime,
      performanceScore: perf.performanceScore,
      jankRatio,
      memoryUsage: perf.memoryUsage
    };
  }, []);

  // Otimizações baseadas em performance
  const applyPerformanceOptimizations = useCallback((metrics: ReturnType<typeof calculatePerformanceMetrics>) => {
    const perf = performanceRef.current;
    
    // Modo de baixa performance
    if (metrics.performanceScore < 60 || metrics.jankRatio > 0.3) {
      perf.lowPerformanceMode = true;
      perf.targetFps = 30;
      perf.frameTime = 33.33;
    } else if (metrics.performanceScore > 80 && metrics.jankRatio < 0.1) {
      perf.lowPerformanceMode = false;
      perf.targetFps = 60;
      perf.frameTime = 16.67;
    }
    
    // Ajuste dinâmico de qualidade baseado na memória
    if (metrics.memoryUsage > 0.8) {
      perf.lowPerformanceMode = true;
      // Reduz qualidade visual em caso de alta utilização de memória
    }
    
    return perf.lowPerformanceMode;
  }, []);

  // Funções de easing avançadas para interpolação ultra-suave
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  const easeInOutSine = (t: number): number => {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  };

  // Sistema de interpolação avançado com múltiplas camadas de suavização e FPS adaptativo
  const startAnimation = useCallback(() => {
    if (animationRef.current) {
      browserCompatibility.cancelFrame(animationRef.current);
    }

    let lastTime = browserCompatibility.getTime();
    let currentVelocity = 0;
    let smoothedVelocity = 0;
    let positionBuffer = 0;
    let frameSkipCounter = 0;
    
    const targetSpeed = speed / 60; // Converte de pixels por minuto para pixels por frame (60fps)
    console.log('Calculated targetSpeed:', targetSpeed, 'from speed:', speed);
     const velocitySmoothing = 0.08; // Suavização da velocidade
     const positionSmoothing = 0.15; // Suavização da posição
     const frameSmoothing = 0.1; // Suavização entre frames

    // Controle de FPS adaptativo integrado com métricas avançadas
     const updatePerformanceMetrics = (currentTime: number) => {
       const perf = performanceRef.current;
       perf.frameCount++;
       
       // Calcula métricas em tempo real a cada frame
       const metrics = calculatePerformanceMetrics(currentTime);
       
       if (currentTime - perf.lastFpsTime >= perf.performanceCheckInterval) {
         perf.currentFps = perf.frameCount;
         perf.frameCount = 0;
         perf.lastFpsTime = currentTime;
         
         // Aplica otimizações baseadas nas métricas
         const isLowPerformance = applyPerformanceOptimizations(metrics);
         
         // Log de performance para debugging (apenas em desenvolvimento)
         if (process.env.NODE_ENV === 'development') {
           console.log('Performance Metrics:', {
             fps: metrics.fps,
             score: Math.round(metrics.performanceScore),
             jankRatio: Math.round(metrics.jankRatio * 100) + '%',
             avgFrameTime: Math.round(metrics.averageFrameTime * 100) / 100 + 'ms',
             lowPerformanceMode: isLowPerformance
           });
         }
       }
       
       return metrics;
     };

    const animate = (currentTime: number) => {
      // Pausa a animação durante scroll manual ou quando pausado
      if (isPausedRef.current || isScrolling || !tickerContentRef.current) {
        lastTime = browserCompatibility.getTime(); // Atualiza o tempo para evitar saltos
        animationRef.current = browserCompatibility.requestFrame(animate) as number;
        return;
      }

      // Controle de FPS adaptativo
      updatePerformanceMetrics(currentTime);
      const perf = performanceRef.current;
      
      // Skip frames em modo de baixa performance
      if (perf.lowPerformanceMode) {
        frameSkipCounter++;
        if (frameSkipCounter % 2 === 0) {
          animationRef.current = browserCompatibility.requestFrame(animate) as number;
          return;
        }
      }

      const deltaTime = Math.min(currentTime - lastTime, 32); // Limita delta para evitar saltos
      const normalizedDelta = deltaTime / perf.frameTime; // Normaliza baseado no FPS alvo
      
      // Primeira camada: interpolação da velocidade alvo com adaptação de FPS
      const targetVelocity = targetSpeed * normalizedDelta;
      currentVelocity += (targetVelocity - currentVelocity) * velocitySmoothing;
      
      // Segunda camada: suavização adicional da velocidade
      smoothedVelocity += (currentVelocity - smoothedVelocity) * frameSmoothing;
      
      // Terceira camada: aplicação de easing na posição
      const rawMovement = smoothedVelocity;
      const easedMovement = rawMovement * easeInOutSine(Math.min(smoothedVelocity / targetSpeed, 1));
      
      // Buffer de posição para suavização final
      positionBuffer += (easedMovement - positionBuffer) * positionSmoothing;
      positionRef.current -= positionBuffer;

      // Cache da largura do conteúdo
      const contentWidth = tickerContentRef.current.scrollWidth;
      
      // Calcula a largura real de uma cópia dos itens (metade do conteúdo total)
      const singleCopyWidth = contentWidth / 2;
      
      // Reset suave com interpolação - quando uma cópia completa passou
      if (Math.abs(positionRef.current) >= singleCopyWidth) {
        // Reset para o início da segunda cópia para continuidade perfeita
        positionRef.current = positionRef.current + singleCopyWidth;
      }

      // Aplicação final com sub-pixel precision e otimizações de performance
      const finalPosition = Math.round(positionRef.current * 100) / 100;
      const transform = `translate3d(${finalPosition}px, 0, 0)`;
      tickerContentRef.current.style.transform = transform;
      
      // Otimizações específicas para performance baseadas no dispositivo e navegador
       const deviceOpts = deviceCapabilities.optimizations;
       const compat = browserCompatibility;
       
       if (!perf.lowPerformanceMode && deviceOpts.useHardwareAcceleration) {
         if (compat.supportsTransform3d) {
           tickerContentRef.current.style.backfaceVisibility = 'hidden';
           tickerContentRef.current.style.perspective = '1000px';
           tickerContentRef.current.style.transformStyle = 'preserve-3d';
         }
       }
       
       // Force hardware acceleration com fallbacks
       if (deviceOpts.useWillChange && compat.supportsWillChange) {
         tickerContentRef.current.style.willChange = 'transform';
       }
       
       if (deviceOpts.enableContainment && compat.supportsContain) {
         tickerContentRef.current.style.contain = 'layout style paint';
       }
       
       // Otimizações específicas para iOS
       if (deviceCapabilities.isIOS) {
         tickerContentRef.current.style.webkitTransform = transform;
         tickerContentRef.current.style.webkitBackfaceVisibility = 'hidden';
       }
       
       // Otimizações específicas para Android
       if (deviceCapabilities.isAndroid) {
         tickerContentRef.current.style.webkitTransform = transform;
       }

      lastTime = currentTime;
      animationRef.current = browserCompatibility.requestFrame(animate) as number;
    };

    animationRef.current = browserCompatibility.requestFrame(animate) as number;
  }, [isScrolling]);

  // Função para pausar/retomar animação
  const togglePause = useCallback(() => {
    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    isPausedRef.current = newPausedState;
    console.log('Animation paused:', newPausedState);
  }, [isPaused]);

  // Iniciar animação quando o componente monta ou a velocidade muda
  useEffect(() => {
    if (displayItems && displayItems.length > 0) {
      startAnimation();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [displayItems, startAnimation, speed]);
  
  // Inicializa os buffers quando há novos itens
  useEffect(() => {
    if (items && items.length > 0) {
      if (!hasInitialLoad) {
        // Primeira carga - inicializa ambos os buffers
        setPrimaryBuffer(items);
        setSecondaryBuffer(items);
        setHasInitialLoad(true);
        stableItemsRef.current = items;
      } else {
        // Atualizações subsequentes - usa o sistema de buffer duplo
        const currentItems = activeBuffer === 'primary' ? primaryBuffer : secondaryBuffer;
        if (JSON.stringify(currentItems) !== JSON.stringify(items)) {
          updateBuffer(items);
          stableItemsRef.current = items;
        }
      }
    }
  }, [items, hasInitialLoad, activeBuffer, primaryBuffer, secondaryBuffer, updateBuffer]);
  
  // Sistema automático de troca de buffers quando há novas notícias
  useEffect(() => {
    if (isBufferReady) {
      // Agenda a troca de buffer para o próximo ciclo de animação completo
      const container = containerRef.current;
      if (container) {
        const tickerContent = container.querySelector('[data-ticker-content]') as HTMLElement;
        if (tickerContent) {
          // Calcula quando a animação atual completará um ciclo
          const computedStyle = window.getComputedStyle(tickerContent);
          const animationDuration = parseFloat(computedStyle.animationDuration) * 1000;
          const transform = computedStyle.transform;
          
          let currentProgress = 0;
          if (transform && transform !== 'none') {
            const matrixValues = transform.match(/matrix.*\((.*)\)/);
            if (matrixValues) {
              const values = matrixValues[1].split(', ');
              const currentPosition = Math.abs(parseFloat(values[4]) || 0);
              const containerWidth = container.offsetWidth;
              currentProgress = (currentPosition / containerWidth) * 100;
            }
          }
          
          // Calcula o tempo restante para completar o ciclo atual
          const remainingProgress = 100 - (currentProgress % 100);
          const timeToComplete = (remainingProgress / 100) * animationDuration;
          
          // Agenda a troca para quando o ciclo atual terminar
          const switchTimeout = setTimeout(() => {
            switchBuffer();
          }, Math.max(timeToComplete, 1000)); // Mínimo de 1 segundo
          
          return () => clearTimeout(switchTimeout);
        }
      }
    }
  }, [isBufferReady, switchBuffer]);
  
  // Sistema robusto de detecção de novos itens
  useEffect(() => {
    if (previousItemsRef.current.length === 0) {
      // Primeira carga - não marcar como novos
      previousItemsRef.current = items;
      return;
    }

    if (items.length > previousItemsRef.current.length) {
      // Detectar novos itens comparando por link/id
      const previousLinks = new Set(previousItemsRef.current.map(item => item.link));
      const newItems = items.filter(item => !previousLinks.has(item.link));
      
      if (newItems.length > 0) {
        console.log(`🆕 ${newItems.length} nova(s) notícia(s) detectada(s):`, newItems.map(item => item.title));
        
        // Atualizar mapa de novos itens
        const newItemsSet = new Set(newItemsMap);
        newItems.forEach(item => {
          newItemsSet.add(item.link);
        });
        setNewItemsMap(newItemsSet);
        
        // Remover marcação de "novo" após 10 segundos
        setTimeout(() => {
          setNewItemsMap(prev => {
            const updated = new Set(prev);
            newItems.forEach(item => {
              updated.delete(item.link);
            });
            return updated;
          });
        }, 10000);
      }
    }
    
    previousItemsRef.current = items;
  }, [items, newItemsMap]);

  // Cálculo otimizado do índice atual com detecção precisa de visibilidade
  useEffect(() => {
    if (!displayItems || displayItems.length === 0) return;

    let itemWidthsCache: { width: number; offset: number }[] = [];
    let lastCacheUpdate = 0;
    const CACHE_DURATION = 2000; // Cache por 2 segundos

    const calculateCurrentIndex = () => {
      if (!tickerContentRef.current || !containerRef.current) return;

      const currentTime = Date.now();
      const currentPosition = Math.abs(positionRef.current);
      const containerWidth = containerRef.current.offsetWidth;
      
      // Atualizar cache de larguras se necessário
      if (currentTime - lastCacheUpdate > CACHE_DURATION || itemWidthsCache.length === 0) {
        const newsItems = tickerContentRef.current.querySelectorAll('[data-news-item]');
        const originalItemsCount = Math.floor(newsItems.length / 2);
        
        itemWidthsCache = [];
        let accumulatedWidth = 0;
        
        for (let i = 0; i < originalItemsCount; i++) {
          const itemElement = newsItems[i] as HTMLElement;
          const width = itemElement.offsetWidth + 80; // margin-right
          itemWidthsCache.push({ width, offset: accumulatedWidth });
          accumulatedWidth += width;
        }
        lastCacheUpdate = currentTime;
      }
      
      // Encontrar o item mais visível no centro da viewport
      let newIndex = 0;
      let maxVisibility = 0;
      const viewportCenter = containerWidth / 2;
      
      for (let i = 0; i < itemWidthsCache.length; i++) {
        const itemData = itemWidthsCache[i];
        const itemStart = itemData.offset - currentPosition;
        const itemEnd = itemStart + itemData.width;
        const itemCenter = itemStart + (itemData.width / 2);
        
        // Calcula a visibilidade do item (0-1)
        const visibleStart = Math.max(0, itemStart);
        const visibleEnd = Math.min(containerWidth, itemEnd);
        const visibleWidth = Math.max(0, visibleEnd - visibleStart);
        const visibility = visibleWidth / itemData.width;
        
        // Prioriza itens mais próximos do centro e mais visíveis
        const distanceFromCenter = Math.abs(itemCenter - viewportCenter);
        const centerScore = Math.max(0, 1 - (distanceFromCenter / containerWidth));
        const totalScore = visibility * 0.7 + centerScore * 0.3;
        
        if (totalScore > maxVisibility && visibility > 0.3) { // Mínimo 30% visível
          maxVisibility = totalScore;
          newIndex = i;
        }
      }
      
      throttledUpdateIndex(newIndex);
      onCurrentItemChange?.(newIndex);
    };

    const interval = setInterval(calculateCurrentIndex, 200); // Mais responsivo
    return () => clearInterval(interval);
  }, [displayItems, onCurrentItemChange, throttledUpdateIndex]);

  // Só mostra "Carregando" se nunca houve carregamento inicial
  if (!hasInitialLoad && (!displayItems || displayItems.length === 0)) {
    return (
      <TickerContainer height={height} backgroundColor={backgroundColor}>
        <TickerLabel textColor={textColor}>NOTÍCIAS</TickerLabel>
        <TickerContent 
          textColor={textColor} 
          isPaused={false}
        >
          <NewsItem>Carregando notícias...</NewsItem>
          <NewsItem>Carregando notícias...</NewsItem>
        </TickerContent>
      </TickerContainer>
    );
  }

  return (
    <TickerContainer ref={containerRef} height={height} backgroundColor={backgroundColor}>
      <TickerLabel textColor={textColor}>NOTÍCIAS</TickerLabel>
      <TickerContent 
        ref={tickerContentRef}
        data-ticker-content 
        textColor={textColor} 
        isPaused={false}
      >
        {/* Primeira cópia dos itens - otimizada */}
         {displayItems.map((item, index) => {
           const isNewItem = newItemsMap.has(item.link);
           const isActiveItem = index === currentItemIndex;
           return (
             <MemoizedNewsItem
               key={`first-${item.link}-${index}`}
               item={item}
               index={index}
               isNew={isNewItem}
               isActive={isActiveItem}
               keyPrefix="first"
             />
           );
         })}
         {/* Segunda cópia dos itens para rolagem contínua - otimizada */}
         {displayItems.map((item, index) => {
           const isNewItem = newItemsMap.has(item.link);
           const isActiveItem = index === currentItemIndex;
           return (
             <MemoizedNewsItem
               key={`second-${item.link}-${index}`}
               item={item}
               index={index}
               isNew={isNewItem}
               isActive={isActiveItem}
               keyPrefix="second"
             />
           );
         })}
      </TickerContent>
    </TickerContainer>
  );
};

export default NewsTicker;